import { PrismicRichText } from '@prismicio/react';
import Image from 'next/image';
import { useState } from 'react';
import ContentContainer from 'remote-com-shared/components/ContentContainer';
import { fluidProp } from 'remote-com-shared/style/helpers';
import styled, { css } from 'styled-components';

import { Form } from './Form';
import BlueArrowIcon from './assets/blue-arrow-right.svg';
import HalfCircleIcons from './assets/half-circles.svg';

import { transformProperties } from '@/src/domains/prismic/helpers';
import {
  desktopBreakpoint,
  mobileBreakpoint,
  tabletBreakpoint,
} from '@/src/domains/shared/breakpoints';
import {
  ContentSectionHeader,
  ContentSectionSplit,
  ContentSectionSplitColumn,
  ContentSectionTitle,
} from '@/src/views/Connect2022/ContentSection';

const ContentSection = styled.section`
  background: #f8f5ff;
  ${fluidProp('padding-top', '110px', '179px')}
  ${fluidProp('padding-bottom', '110px', '148px')}
  position: relative;
`;

export const SectionHeader = styled(ContentSectionHeader)`
  ${desktopBreakpoint(css`
    margin-top: 30px;
  `)}
`;

const SectionSplitColumn = styled(ContentSectionSplitColumn)`
  width: 50%;

  ${desktopBreakpoint(css`
    width: 100%;
  `)}
`;

export const ContentSectionSubTitle = styled.div`
  ${({ theme }) => theme.typography.titleXL}
  color: var(--colors-darkBlue);

  > * {
    font-size: inherit;
    font-weight: inherit;
    line-height: inherit;
    margin: inherit;
  }
`;

export const ContentSectionBackground = styled.div`
  position: absolute;
  right: 0;
  bottom: -310px;
  z-index: 0;
  width: 600px;
  height: auto;

  ${desktopBreakpoint(css`
    display: none;
  `)}
`;

const SectionSplit = styled(ContentSectionSplit)`
  display: flex;
  position: relative;
  z-index: 1;

  ${desktopBreakpoint(css`
    flex-direction: column;
  `)}
`;

export const SectionTitle = styled(ContentSectionTitle)`
  color: #5700ff;
  ${({ theme }) => theme.typography.scale3};
  ${fluidProp('font-size', '32px', '42px')}
  font-family: 'Bossa', sans-serif;

  > * {
    font-size: inherit;
    font-weight: inherit;
    line-height: inherit;
  }
`;

export const ContentSectionDescription = styled.div`
  color: var(--colors-bayoux);
  ${({ theme }) => theme.typography.bodyLead};

  > * {
    font-size: inherit;
    font-weight: inherit;
    line-height: inherit;
  }
`;

const ContentSectionLabel = styled.div`
  ${({ theme }) => theme.typography.bodyBold};
  ${fluidProp('line-height', '34px', '43px')}
  text-transform: uppercase;
  color: #0061ff;
  display: flex;
  font-family: 'Bossa', sans-serif;

  p {
    margin: 0 0 7px;
  }
`;

const IconWrapper = styled.div`
  margin-right: 8px;
`;

const ImageContainer = styled.div`
  display: flex;
  justify-content: center;
`;

const IllustrationWrapper = styled.div`
  position: absolute;
  left: 0;
  top: 10%;
  z-index: 0;

  ${mobileBreakpoint(css`
    display: none;
  `)}
`;

export const ApplyFormColumn = styled(SectionSplitColumn)`
  width: 435px;

  ${desktopBreakpoint(css`
    margin: 0 auto;
    max-width: 435px;
  `)}

  ${tabletBreakpoint(css`
    width: 95%;
  `)}
`;

export const ApplyFormWrapper = styled.div`
  ${fluidProp('--spacing-x', '20px', '48px')}
  ${fluidProp('--spacing-y', '26px', '56px')}
  padding: var(--spacing-y) var(--spacing-x);
  background-color: var(--colors-blank);
  box-shadow: 0px 12px 48px rgba(96, 101, 123, 0.24);
  border-radius: 10px;
`;

export function OverviewFormSlice({ slice, context: { applyFormRef } }) {
  const { primary, context } = transformProperties(slice);
  const { title, description, label, formTitle, eventName } = primary;

  const { hubspotFormDefinition } = context;

  const [showSuccessState, setShowSuccessState] = useState(false);

  return (
    <ContentSection>
      <ContentContainer>
        <SectionSplit>
          <SectionSplitColumn>
            {label && (
              <ContentSectionLabel>
                <IconWrapper>
                  <BlueArrowIcon />
                </IconWrapper>
                <p>{label}</p>
              </ContentSectionLabel>
            )}
            {title && (
              <SectionTitle>
                <PrismicRichText field={title} />
              </SectionTitle>
            )}
            {description && (
              <ContentSectionDescription>
                <PrismicRichText field={description} />
              </ContentSectionDescription>
            )}
          </SectionSplitColumn>
          <ApplyFormColumn>
            <ApplyFormWrapper ref={applyFormRef}>
              {!showSuccessState && (
                <SectionHeader>
                  <ContentSectionSubTitle>
                    <PrismicRichText field={formTitle} />
                  </ContentSectionSubTitle>
                </SectionHeader>
              )}
              <Form
                hubspotFormDefinition={hubspotFormDefinition}
                onSuccessChange={setShowSuccessState}
                eventName={eventName}
              />
            </ApplyFormWrapper>
          </ApplyFormColumn>
        </SectionSplit>
      </ContentContainer>
      <ContentSectionBackground>
        <Image
          src={primary.backgroundIllustration.url}
          {...primary.backgroundIllustration.dimensions}
          alt={primary.backgroundIllustration.alt || ''}
          layout="responsive"
        />
      </ContentSectionBackground>
      <IllustrationWrapper>
        <ImageContainer>
          <HalfCircleIcons />
        </ImageContainer>
      </IllustrationWrapper>
    </ContentSection>
  );
}
